// ============================================================================
// MarketAddressBook — «Мои адреса» клиента (user_settings).
// Отдельного справочника адресов в поставке hydra.market нет: адресная книга
// хранится в персональных настройках и подставляется в мастер заявки.
// Экран мобильный: карточки, без таблиц и окон платформы.
// ============================================================================

import { useState } from 'react';
import { CheckCircle2, MapPin, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  composeMarketAddress,
  deleteMarketAddress,
  loadMarketAddresses,
  saveMarketAddress,
  setDefaultMarketAddress,
  type MarketSavedAddress,
} from '@/lib/marketProfile';
import { useToastStore } from '@/store/toastStore';

interface AddressFormState {
  id: string;
  label: string;
  city: string;
  district: string;
  address: string;
}

const EMPTY_FORM: AddressFormState = { id: '', label: '', city: '', district: '', address: '' };

export function MarketAddressBook() {
  const [addresses, setAddresses] = useState<MarketSavedAddress[]>(() => loadMarketAddresses());
  const [form, setForm] = useState<AddressFormState | null>(null);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const { showSuccess, showError } = useToastStore();

  const add = () => {
    setForm({ ...EMPTY_FORM });
    setFormError('');
    setDeleteId('');
  };

  const edit = (address: MarketSavedAddress) => {
    setForm({
      id: address.id,
      label: address.label,
      city: address.city,
      district: address.district,
      address: address.address,
    });
    setFormError('');
    setDeleteId('');
  };

  const submit = () => {
    if (!form) return;
    setFormError('');
    try {
      const next = saveMarketAddress({
        id: form.id || undefined,
        label: form.label,
        city: form.city,
        district: form.district,
        address: form.address,
      });
      setAddresses(next);
      setForm(null);
      showSuccess(form.id ? 'Адрес обновлён' : 'Адрес добавлен');
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: сохранение адреса');
      setFormError(friendlyError(e));
    }
  };

  const remove = (id: string) => {
    try {
      setAddresses(deleteMarketAddress(id));
      setDeleteId('');
      showSuccess('Адрес удалён');
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: удаление адреса');
      showError(friendlyError(e));
    }
  };

  const makeDefault = (id: string) => {
    try {
      setAddresses(setDefaultMarketAddress(id));
      showSuccess('Адрес выбран основным');
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: основной адрес');
      showError(friendlyError(e));
    }
  };

  return (
    <section className="space-y-3 rounded-2xl border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Мои адреса</h2>
          <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground">
            Адреса подставляются при создании заявки — не нужно вводить заново.
          </p>
        </div>
        {!form && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 rounded-lg text-[11px]"
            onClick={add}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Добавить
          </Button>
        )}
      </div>

      {form && (
        <div className="space-y-3 rounded-xl border bg-background/70 p-3">
          <div className="space-y-1.5">
            <Label htmlFor="market-address-label">Название (необязательно)</Label>
            <Input
              id="market-address-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Например: Дом, Работа"
              className="h-10 rounded-lg"
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="market-address-city">Город</Label>
              <Input
                id="market-address-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="h-10 rounded-lg"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="market-address-district">Район</Label>
              <Input
                id="market-address-district"
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
                className="h-10 rounded-lg"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="market-address-text">Адрес</Label>
            <textarea
              id="market-address-text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={3}
              placeholder="Улица, дом, квартира, подъезд"
              className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-base leading-6 outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
            />
          </div>
          {formError && <div className="text-[11px] text-destructive">{formError}</div>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="h-10 flex-1 rounded-lg" onClick={() => setForm(null)}>
              Отмена
            </Button>
            <Button type="button" className="h-10 flex-1 rounded-lg" onClick={submit}>
              Сохранить адрес
            </Button>
          </div>
        </div>
      )}

      {addresses.length === 0 && !form ? (
        <div className="rounded-xl border border-dashed bg-background/50 px-4 py-6 text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MapPin className="h-5 w-5" />
          </div>
          <div className="text-sm font-medium">Адресов пока нет</div>
          <div className="mx-auto mt-1 max-w-xs text-[11px] leading-5 text-muted-foreground">
            Добавьте домашний или рабочий адрес — он появится при создании заявки.
          </div>
          <Button type="button" size="sm" className="mt-3 h-9 rounded-lg" onClick={add}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Добавить адрес
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {addresses.map((address) => {
            const full = composeMarketAddress(address);
            return (
              <div key={address.id} className="rounded-xl border bg-background/70 p-3">
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium">{address.label}</span>
                      {address.isDefault && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                          <Star className="h-2.5 w-2.5" />
                          основной
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 break-words text-[11px] leading-5 text-muted-foreground">
                      {full}
                    </div>
                  </div>
                </div>

                {deleteId === address.id ? (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-2 py-1.5">
                    <span className="min-w-0 flex-1 text-[11px] text-destructive">Удалить этот адрес?</span>
                    <Button type="button" variant="destructive" size="sm" className="h-7 rounded-md text-[10px]" onClick={() => remove(address.id)}>
                      Удалить
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-7 rounded-md text-[10px]" onClick={() => setDeleteId('')}>
                      Отмена
                    </Button>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {!address.isDefault && (
                      <Button type="button" variant="outline" size="sm" className="h-7 rounded-md text-[10px]" onClick={() => makeDefault(address.id)}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Основной
                      </Button>
                    )}
                    <Button type="button" variant="outline" size="sm" className="h-7 rounded-md text-[10px]" onClick={() => edit(address)}>
                      <Pencil className="mr-1 h-3 w-3" />
                      Изменить
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 rounded-md text-[10px] text-destructive" onClick={() => setDeleteId(address.id)}>
                      <Trash2 className="mr-1 h-3 w-3" />
                      Удалить
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] leading-4 text-muted-foreground">
        Район сохраняется в профиле и подставляется в адрес заявки. Адреса хранятся в ваших настройках
        и доступны только вам.
      </p>
    </section>
  );
}
